import React, { useCallback, useMemo, useState } from "react";

import { useQuery } from "@apollo/client";
import { useDebounce } from "react-use";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";

import qs from "query-string";
import dayjs from "dayjs";

import Select from "react-select";
import Button from "react-bootstrap/Button";
import DatePicker from "react-datepicker";

import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import { QUERY_UPCOMING_MEETING_ATTENDEES } from "../../../../config/graphql/query";

import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";

const limit = 10;

type TPreRegStatusFilter = PreRegistrationFilterStatus;

const statusCellClass = (
  status: PreRegistrationTokenStatus | "no_token",
): string => {
  switch (status) {
    case "completed":
      return "bg-success text-white";
    case "pending":
      return "bg-warning text-white";
    case "expired":
      return "bg-danger text-white";
    case "no_token":
    default:
      return "bg-light text-dark";
  }
};

const resolveStatus = (
  item: IUpcomingMeetingAttendee,
): PreRegistrationTokenStatus | "no_token" => {
  return item.attendee.preRegistrationToken?.status || "no_token";
};

const TableRow = ({ item }: { item: IUpcomingMeetingAttendee }) => {
  const { t } = useTranslation(["preRegistrations", "common"]);

  const { id, attendee, meeting, hasCheckedIn } = item;
  const status = resolveStatus(item);

  return (
    <tr>
      <td>{attendee.fullName}</td>
      <td>{attendee.email}</td>
      <td>{dayjs(meeting.dtstart).local().format("DD-MM-YYYY / HH:mm")}</td>
      <td>{meeting.organizer?.fullName || ""}</td>
      <td className={statusCellClass(status)}>
        {t(`preRegistrations:status.${status}`).toUpperCase()}
        {hasCheckedIn &&
          ` / ${t("preRegistrations:status.checkedIn").toUpperCase()}`}
      </td>
      <td className="text-nowrap">
        <div className="d-flex">
          <Link to={`/activity/preRegistrations/${id}`}>
            <Button size="sm" variant="primary">
              {t("common:view")}
            </Button>
          </Link>
        </div>
      </td>
    </tr>
  );
};

const PreRegistrations = React.memo(() => {
  const location = useLocation();
  const history = useHistory();

  const { t } = useTranslation(["preRegistrations", "common"]);

  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as {
        page?: number;
        search?: string;
        from?: string;
        to?: string;
        preRegStatus?: TPreRegStatusFilter;
        hideCheckedIn?: string;
      },
    [location.search],
  );

  const [search, setSearch] = useState(query.search);

  const [startDate, setStartDate] = useState<Date | undefined>(
    query.from ? dayjs(query.from).toDate() : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    query.to ? dayjs(query.to).toDate() : undefined,
  );

  const [preRegStatus, setPreRegStatus] = useState<
    TPreRegStatusFilter | undefined
  >(query.preRegStatus);
  const [hideCheckedIn, setHideCheckedIn] = useState<boolean>(
    query.hideCheckedIn !== "false",
  );

  const page = useMemo(() => Math.max(query.page || 0, 1), [query.page]);

  const statusOptions = useMemo<
    { value: TPreRegStatusFilter; label: string }[]
  >(
    () => [
      { value: "pending", label: t("preRegistrations:status.pending") },
      { value: "completed", label: t("preRegistrations:status.completed") },
      { value: "expired", label: t("preRegistrations:status.expired") },
      { value: "no_token", label: t("preRegistrations:status.no_token") },
    ],
    [t],
  );

  const variables = useMemo(() => {
    return {
      pagination: {
        limit,
        skip: (page - 1) * limit,
      },
      filter: {
        ...(query.search && { search: query.search.toString() }),
        ...(query.from && {
          startDate: dayjs(query.from)
            .hour(0)
            .minute(0)
            .second(0)
            .toISOString(),
        }),
        ...(query.to && {
          endDate: dayjs(query.to).hour(23).minute(59).second(59).toISOString(),
        }),
        ...(query.preRegStatus && { preRegStatus: query.preRegStatus }),
        hideCheckedIn: query.hideCheckedIn !== "false",
      },
      sort: { dtstart: "ASC" },
    };
  }, [
    page,
    query.search,
    query.from,
    query.to,
    query.preRegStatus,
    query.hideCheckedIn,
  ]);

  const { data } = useQuery<{
    upcomingMeetingAttendees?: IUpcomingMeetingAttendeesList;
  }>(QUERY_UPCOMING_MEETING_ATTENDEES, {
    fetchPolicy: "network-only",
    variables,
  });

  const items: IUpcomingMeetingAttendee[] =
    data?.upcomingMeetingAttendees?.items ?? [];
  const totalCount: number = data?.upcomingMeetingAttendees?.totalCount ?? 0;

  const renderItem = useCallback(
    (item: IUpcomingMeetingAttendee) => <TableRow key={item.id} item={item} />,
    [],
  );

  useDebounce(
    () => {
      history.push({
        search: qs.stringify({
          page: 1,
          ...(search && { search }),
          ...(startDate && {
            from: dayjs(startDate).format("YYYY-MM-DD"),
          }),
          ...(endDate && { to: dayjs(endDate).format("YYYY-MM-DD") }),
          ...(preRegStatus && { preRegStatus }),
          ...(!hideCheckedIn && { hideCheckedIn: "false" }),
        }),
      });
    },
    500,
    [search, startDate, endDate, preRegStatus, hideCheckedIn],
  );

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap justify-content-between mt-4">
        <form className="form-inline">
          <div className="mr-5 mb-4">
            <label htmlFor="search" className="sr-only">
              {t("common:search")}
            </label>
            <input
              id="search"
              type="search"
              className="form-control"
              placeholder={t("common:search")}
              value={search ?? ""}
              onChange={({ target: { value } }) => {
                setSearch(value);
              }}
            />
          </div>
          <div className="d-flex mb-4 mr-0 mr-sm-5">
            <div className="d-flex mr-4">
              <div className="mr-2 d-flex align-items-center">
                {t("preRegistrations:filter.startDate")}
              </div>
              <DatePicker
                isClearable
                selected={startDate}
                onChange={(date: Date) => {
                  setStartDate(date);
                }}
                placeholderText="Start Date"
                dateFormat="MM/dd/yyyy"
                className="form-control"
                todayButton="Today"
              />
            </div>
            <div className="d-flex">
              <div className="mr-2 d-flex align-items-center">
                {t("preRegistrations:filter.endDate")}
              </div>
              <DatePicker
                isClearable
                selected={endDate}
                onChange={(date: Date) => {
                  setEndDate(date);
                }}
                placeholderText="End Date"
                dateFormat="MM/dd/yyyy"
                className="form-control"
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row">
            <div className="d-flex mb-4 mr-3">
              <label htmlFor="status" className="mb-0 mr-2 align-self-center">
                {t("preRegistrations:filter.status")}
              </label>
              <div style={{ width: "13rem" }}>
                <Select
                  isClearable
                  options={statusOptions}
                  value={statusOptions.find(
                    (option) => option.value === preRegStatus,
                  )}
                  onChange={(value) => setPreRegStatus(value?.value)}
                  styles={reactSelectCustomStyles(false)}
                />
              </div>
            </div>
            <div className="d-flex mb-4 align-items-center">
              <input
                id="hideCheckedIn"
                type="checkbox"
                className="form-check-input ml-0 mr-2 position-static"
                checked={hideCheckedIn}
                onChange={({ target: { checked } }) =>
                  setHideCheckedIn(checked)
                }
              />
              <label htmlFor="hideCheckedIn" className="mb-0">
                {t("preRegistrations:filter.hideCheckedIn")}
              </label>
            </div>
          </div>
        </form>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col">{t("preRegistrations:th.fullName")}</th>
            <th scope="col">{t("preRegistrations:th.email")}</th>
            <th scope="col">{t("preRegistrations:th.meetingTime")}</th>
            <th scope="col">{t("preRegistrations:th.organizer")}</th>
            <th scope="col">{t("preRegistrations:th.status")}</th>
            <th scope="col">{t("preRegistrations:th.actions")}</th>
          </tr>
        </thead>
        <tbody>{items.map(renderItem)}</tbody>
      </Table>
      <Pagination documentsCount={totalCount} limit={limit} />
    </div>
  );
});

export default PreRegistrations;
